import {action, observable} from 'mobx';

import {EXPIRE_TIME, EXPIRES_IN, EXPIRES_PENDING} from '../../../constants/storeConstants';
import {STATE_FULFILLED, STATE_PENDING, STATE_REJECTED} from '../../../constants/asyncConstants';
import serverApi from '../../../utils/serverApi';
import ApiBaseStore from '../common/apiBaseStore';

/**
 * The default query used when none is given.
 *
 * @type {{page: number, perPage: number, search: string, sort: string, direction: string}}
 */
export const DEFAULT_CONTENT_REQUESTS_QUERY = {
  page: 1,
  perPage: 20,
  search: '',
  sort: 'createdAt',
  direction: 'desc',
};

/**
 * ApiAdminContentRequestGetAllStore
 * Holds a single page of content requests from the v2 API.
 */
class ApiAdminContentRequestGetAllStore extends ApiBaseStore {
  /**
   * Pagination meta from the API ({currentPage, lastPage, perPage, total, ...}).
   *
   * @type {?{}}
   */
  @observable meta = null;

  /**
   * Incremented on each request so responses that arrive out of order are ignored.
   *
   * @type {number}
   */
  requestCounter = 0;

  /**
   * Clears all the store's info.
   */
  @action clear() {
    super.clear();
    this.meta = null;
  }

  /**
   * Always fetches the given page (pagination params change too often for caching to help).
   *
   * @param {{}} query
   */
  refresh(query) {
    this.makeRequest(query);
  }

  /**
   * Fetches a page of content requests.
   *
   * @param {{page: number?, perPage: number?, search: string?, sort: string?, direction: string?}} query
   */
  @action makeRequest(query = {}) {
    this.requestCounter += 1;
    const requestId = this.requestCounter;
    const safeQuery = {...DEFAULT_CONTENT_REQUESTS_QUERY, ...query};

    this.state = STATE_PENDING;
    this[EXPIRE_TIME] = Date.now() + EXPIRES_PENDING;
    this.error = null;

    serverApi.contentRequestsGet(safeQuery).then(
      action('adminContentRequestGetAllSuccess', (response) => {
        if (requestId !== this.requestCounter) {
          return;
        }

        this.state = STATE_FULFILLED;
        this[EXPIRE_TIME] = Date.now() + EXPIRES_IN;
        this.data = (response && response.data) || [];
        this.meta = (response && response.meta) || null;
        this.error = null;
      }),
      action('adminContentRequestGetAllError', (requestsGetAllError) => {
        if (requestId !== this.requestCounter) {
          return;
        }

        this.state = STATE_REJECTED;
        this[EXPIRE_TIME] = null;
        this.data = null;
        this.meta = null;
        this.error = requestsGetAllError;
      })
    );
  }
}

export default new ApiAdminContentRequestGetAllStore();
